// =============================================================================
// src/features/analytics/analytics-ingestion-markup.ts - Aba Configuracoes.
//
// Pedido do usuario (2026-07-25): "nas configuracoes conecte as palavras-chave,
// dimensoes, recorrencias e filtros que o Dagster hoje esta utilizando na
// ingestao". Renderiza o ESPELHO somente-leitura da config real (021 —
// config.watch_query + v_ingestion_*); sem live, mantem o card mock original.
// =============================================================================

import { escapeHtml } from '../../app/dom';
import type {
  OmarAnalyticsIngestionCadence,
  OmarAnalyticsIngestionConfig,
  OmarAnalyticsIngestionProfile,
  OmarAnalyticsIngestionProfileGroup,
  OmarAnalyticsIngestionTheme,
  OmarAnalyticsOutletScopeGroup,
  OmarAnalyticsScopeTermGroup,
} from './analytics-data';
import { getAnalyticsIngestionConfig } from './analytics-live-store';

// Painel "Recorrencias de coleta" OCULTO por hora (pedido do usuario
// 2026-07-25): a cadencia por perfil continua visivel na lista de perfis; o
// painel agregado (fila + ticks) sai da tela. Dados seguem chegando no fetch
// (v_ingestion_cadence) — para religar, flipar esta constante.
const ANALYTICS_SHOW_INGESTION_CADENCE = false;

/** Ticks fixos do pipeline (constantes de pipeline/assets/social_listening/
 * schedules.py — nao existem no banco; manter em sincronia ao mudar os crons). */
const ANALYTICS_PIPELINE_TICKS: readonly (readonly [string, string])[] = [
  ['Noticias (Google News RSS)', 'a cada 30 min'],
  ['Descoberta SERP (dorks pagos)', 'diario, 05h'],
  ['Normalizacao + filtro de relevancia', 'a cada 15 min'],
  ['Comentarios (2o estagio)', 'fila re-agendada por tier de atividade'],
];

/** Renderiza a aba Configuracoes com a config real; null → chamador usa o mock. */
export function buildAnalyticsIngestionViewMarkup(): string | null {
  const ingestionConfig = getAnalyticsIngestionConfig();
  if (!ingestionConfig) return null;
  return `<section class="u-analytics-module" data-analytics-module-view="settings" aria-labelledby="analytics-settings-title" hidden>
    ${analyticsIngestionHeaderMarkup()}
    <section class="u-analytics-grid u-analytics-ingestion-grid">
      ${analyticsIngestionThemesPanelMarkup(ingestionConfig.themes)}
      ${analyticsIngestionProfilesPanelMarkup(ingestionConfig.profileGroups)}
      ${ANALYTICS_SHOW_INGESTION_CADENCE ? analyticsIngestionCadencePanelMarkup(ingestionConfig.cadences) : ''}
      ${analyticsIngestionFiltersPanelMarkup(ingestionConfig)}
    </section>
  </section>`;
}

function analyticsIngestionHeaderMarkup(): string {
  return `<div class="u-analytics-brief"><div><div class="u-analytics-eyebrow">Configuracoes - espelho da ingestao ao vivo</div>
    <h1 id="analytics-settings-title">O que o monitoramento coleta e com quais filtros.</h1></div>
    <p>Leitura direta da configuracao que o pipeline (Dagster) usa agora: temas e palavras-chave, perfis por papel
    e os filtros de escopo Amazonas. Edicao e feita pela curadoria, nao por esta tela.</p></div>`;
}

function analyticsIngestionThemesPanelMarkup(themes: readonly OmarAnalyticsIngestionTheme[]): string {
  return `<section class="u-analytics-panel u-analytics-ingestion-panel is-wide"><div class="u-analytics-panel-head">
    <div><span>Temas e palavras-chave</span><h2>Dimensoes de monitoramento</h2></div><b>${themes.length}</b></div>
    <div class="u-analytics-ingestion-theme-grid">${themes.map(analyticsIngestionThemeMarkup).join('')}</div>
  </section>`;
}

function analyticsIngestionThemeMarkup(theme: OmarAnalyticsIngestionTheme): string {
  const statusBadge = theme.isActive ? '' : '<em class="u-analytics-ingestion-off">inativo</em>';
  const paidBadge = theme.searchEnabled ? '<em class="u-analytics-ingestion-paid">busca paga</em>' : '';
  // Banco guarda hashtag com E sem '#' (seeds diferentes) — normaliza p/ um só.
  const hashtagChips = theme.hashtags.map((hashtag) => `<span>#${escapeHtml(hashtag.replace(/^#+/, ''))}</span>`).join('');
  return `<article class="u-analytics-ingestion-theme">
    <header><strong>${escapeHtml(theme.themeLabel)}</strong>${paidBadge}${statusBadge}</header>
    <div class="u-analytics-tags">${theme.terms.map((term) => `<span>${escapeHtml(term)}</span>`).join('')}${hashtagChips}</div>
    ${analyticsIngestionPaidQueriesMarkup(theme)}
    <small>${escapeHtml(theme.platformLabels.join(' · '))} · geo ${escapeHtml(theme.geo)}</small>
  </article>`;
}

function analyticsIngestionPaidQueriesMarkup(theme: OmarAnalyticsIngestionTheme): string {
  if (theme.paidQueries.length === 0) return '';
  const queryRows = theme.paidQueries
    .map((paidQuery) => `<div><dt>${escapeHtml(paidQuery.platformLabel)}</dt><dd>${escapeHtml(paidQuery.queryLabel)}</dd></div>`)
    .join('');
  return `<dl class="u-analytics-ingestion-queries">${queryRows}</dl>`;
}

function analyticsIngestionProfilesPanelMarkup(profileGroups: readonly OmarAnalyticsIngestionProfileGroup[]): string {
  const totalProfiles = profileGroups.reduce((sum, profileGroup) => sum + profileGroup.profiles.length, 0);
  return `<section class="u-analytics-panel u-analytics-ingestion-panel"><div class="u-analytics-panel-head">
    <div><span>Perfis monitorados</span><h2>Quem entra na coleta</h2></div><b>${totalProfiles}</b></div>
    ${profileGroups.map(analyticsIngestionProfileGroupMarkup).join('')}
  </section>`;
}

function analyticsIngestionProfileGroupMarkup(profileGroup: OmarAnalyticsIngestionProfileGroup): string {
  return `<div class="u-analytics-ingestion-group"><span class="u-analytics-ingestion-group-title">${escapeHtml(profileGroup.roleLabel)}</span>
    ${profileGroup.profiles.map(analyticsIngestionProfileMarkup).join('')}
  </div>`;
}

function analyticsIngestionProfileMarkup(profile: OmarAnalyticsIngestionProfile): string {
  // Recorrencia por perfil OCULTA (pedido 2026-07-25, segunda rodada) e, desde
  // 2026-09-30, tambem o estado 'pausado'/cinza dos inativos: quem ve a tela nao
  // deve enxergar perfil desativado. isActive e cadenceLabel seguem no shape.
  return `<div class="u-analytics-ingestion-profile"><strong>${escapeHtml(profile.actorName)}</strong>
    <span>${escapeHtml(profile.platformLabel)} ${escapeHtml(profile.handle)}</span></div>`;
}

function analyticsIngestionCadencePanelMarkup(cadences: readonly OmarAnalyticsIngestionCadence[]): string {
  return `<section class="u-analytics-panel u-analytics-ingestion-panel"><div class="u-analytics-panel-head">
    <div><span>Recorrencias de coleta</span><h2>Frequencia real da fila</h2></div><i class="ti ti-clock-play"></i></div>
    ${cadences.map(analyticsIngestionCadenceMarkup).join('')}
    <div class="u-analytics-ingestion-group"><span class="u-analytics-ingestion-group-title">Ticks do pipeline</span>
    ${ANALYTICS_PIPELINE_TICKS.map(([tickLabel, tickCadence]) => `<div class="u-analytics-ingestion-profile"><strong>${escapeHtml(tickLabel)}</strong><em>${escapeHtml(tickCadence)}</em></div>`).join('')}
    </div>
  </section>`;
}

function analyticsIngestionCadenceMarkup(cadence: OmarAnalyticsIngestionCadence): string {
  return `<div class="u-analytics-ingestion-profile"><strong>${escapeHtml(cadence.kindLabel)}</strong>
    <span>${escapeHtml(cadence.platformLabel)} · ${cadence.activeTargets}/${cadence.totalTargets} alvos ativos</span>
    <em>${escapeHtml(cadence.cadenceLabel)}</em></div>`;
}

function analyticsIngestionFiltersPanelMarkup(ingestionConfig: OmarAnalyticsIngestionConfig): string {
  return `<section class="u-analytics-panel u-analytics-ingestion-panel is-wide"><div class="u-analytics-panel-head">
    <div><span>Filtros da ingestao</span><h2>Escopo Amazonas aplicado na coleta</h2></div><i class="ti ti-filter-cog"></i></div>
    <div class="u-analytics-ingestion-filters">
      ${ingestionConfig.scopeTermGroups.map(analyticsIngestionScopeGroupMarkup).join('')}
      ${ingestionConfig.outletScopeGroups.map(analyticsIngestionOutletGroupMarkup).join('')}
      ${analyticsIngestionBlockedMarkup(ingestionConfig)}
    </div>
  </section>`;
}

function analyticsIngestionScopeGroupMarkup(scopeGroup: OmarAnalyticsScopeTermGroup): string {
  return `<article class="u-analytics-ingestion-filter-card"><header><strong>${escapeHtml(scopeGroup.kindLabel)}</strong><b>${scopeGroup.termCount}</b></header>
    <div class="u-analytics-tags">${scopeGroup.sampleTerms.map((term) => `<span>${escapeHtml(term)}</span>`).join('')}</div></article>`;
}

function analyticsIngestionOutletGroupMarkup(outletGroup: OmarAnalyticsOutletScopeGroup): string {
  return `<article class="u-analytics-ingestion-filter-card"><header><strong>${escapeHtml(outletGroup.scopeLabel)}</strong><b>${outletGroup.outletCount}</b></header>
    <div class="u-analytics-tags">${outletGroup.sampleOutlets.map((outlet) => `<span>${escapeHtml(outlet)}</span>`).join('')}</div></article>`;
}

function analyticsIngestionBlockedMarkup(ingestionConfig: OmarAnalyticsIngestionConfig): string {
  if (ingestionConfig.blockedAuthors.length === 0) return '';
  return `<article class="u-analytics-ingestion-filter-card"><header><strong>Autores bloqueados (curadoria)</strong><b>${ingestionConfig.blockedAuthors.length}</b></header>
    <div class="u-analytics-tags">${ingestionConfig.blockedAuthors.map((blockedAuthor) => `<span>${escapeHtml(blockedAuthor.platformLabel)} ${escapeHtml(blockedAuthor.handle)}</span>`).join('')}</div></article>`;
}
